import { ComponentType, ReactElement, ReactNode } from 'react';

import {
  attachComponentData,
  getComponentData,
  useElementFilter,
} from '@backstage/core-plugin-api';

import { PAGE_CONTENT_KEY } from '../Content/composition';

export const PAGE_WRAPPER_KEY = 'page.wrapper';

export const asPageWrapper = <P>(
  component: ComponentType<P>,
  wrapperComponent: ComponentType<P & { children: ReactNode }>,
): ComponentType<P> => {
  attachComponentData(component, PAGE_WRAPPER_KEY, wrapperComponent);
  return component;
};

export type WrapperEntry = {
  element: ReactElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed wrapper markers (e.g. Page.FiltersProvider) store the target component here; props are heterogeneous by design.
  component?: ComponentType<any>;
};

export type PageRootChildren = {
  contentElement: ReactElement | null;
  wrapperElements: WrapperEntry[];
};

export const usePageRootChildren = (children: ReactNode): PageRootChildren => {
  return useElementFilter(children, elements => {
    let contentElement: ReactElement | null = null;
    const wrapperElements: WrapperEntry[] = [];

    elements.getElements().forEach(element => {
      const isContent = getComponentData<boolean>(element, PAGE_CONTENT_KEY);
      const wrapperData = getComponentData<unknown>(element, PAGE_WRAPPER_KEY);

      if (isContent) {
        contentElement = element;
      } else if (wrapperData) {
        wrapperElements.push({
          element,
          component:
            typeof wrapperData === 'function'
              ? (wrapperData as ComponentType<unknown>)
              : undefined,
        });
      }
    });

    return { contentElement, wrapperElements };
  });
};
